import { useState, type MouseEvent, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LayersRowMenu } from '@/common/presentation/components/layers';
import type { LayersRowMenuItem } from '@/common/presentation/components/layers';
import RwIconButton from '@/common/presentation/components/rw/RwIconButton';

export interface ElementSelectionChromeProps {
  name: string;
  menuItems?: LayersRowMenuItem[];
  /** A real drag handle for the name tab; without one the tab shows a decorative grip. */
  handle?: ReactNode;
  /** Inline tools drawn left of the ⋮ menu (block editor: width / orientation, `{}`). */
  tools?: ReactNode;
  testId: string;
}

/**
 * The selected-element decoration (Figma `29:18`): a 2px navy outline drawn as `boxShadow` (never
 * `border` — it must not add to layout size, since `EditorInsetFrame` already reserves the space
 * this chrome paints inside), a navy "⠿ <name>" tab pinned to the top-left, and a ⋮ element menu
 * pinned outside the box at the top-right. Rendered only while the element is selected — see
 * `SelectableElement`, the only caller.
 */
export function ElementSelectionChrome({ name, menuItems, handle, tools, testId }: ElementSelectionChromeProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasMenu = Boolean(menuItems?.length);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          boxShadow: 'inset 0 0 0 2px var(--border-focus)',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 'calc(-1 * var(--size-24))',
          left: -2,
          height: 'var(--size-24)',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--size-4, 4px)',
          bgcolor: 'var(--border-focus)',
          color: 'var(--text-on-color-body)',
          px: 'var(--size-6, 6px)',
          borderRadius: 'var(--border-radius-none)',
          zIndex: 2
        }}
        data-testid={`${testId}:name-tab`}
      >
        {handle ?? <DragIndicatorIcon sx={{ fontSize: 12 }} aria-hidden />}
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>
          {name}
        </Typography>
      </Box>

      {tools ? (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(-1 * var(--size-24))',
            right: hasMenu ? 'var(--size-24)' : 'calc(-1 * var(--size-2))',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            zIndex: 4,
            scrollMarginTop: '64px'
          }}
          data-testid={`${testId}:tools`}
        >
          {tools}
        </Box>
      ) : null}

      {hasMenu ? (
        <>
          <RwIconButton
            tier="primary"
            size="sm"
            footprintWidth="var(--size-24)"
            onClick={openMenu}
            aria-label={`More actions — ${name}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            data-testid={`${testId}:element-menu`}
            sx={{
              position: 'absolute',
              top: 'calc(-1 * var(--size-24))',
              right: 'calc(-1 * var(--size-2))',
              height: 'var(--size-24)',
              zIndex: 2,
              bgcolor: 'var(--border-focus)',
              color: 'var(--icon-on-color)',
              // The design system's hover/open fill for this button is a pale grey: the white icon on it measured 1.8:1. Keep the tab's
              // own fill in every state and show hover and open by brightening it.
              // `&&&&` outweighs the stylesheet's `.rwb--icon-only[aria-expanded]` and `:hover` fills.
              '&&&&:hover, &&&&:focus-visible, &&&&[aria-expanded="true"]': {
                bgcolor: 'var(--border-focus)',
                color: 'var(--icon-on-color)',
                filter: 'brightness(1.3)'
              }
            }}
          >
            <MoreVertIcon />
          </RwIconButton>
          <LayersRowMenu
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            items={menuItems ?? []}
            testId={`${testId}:element-menu-list`}
          />
        </>
      ) : null}
    </>
  );
}
